import { rewardsAuthApiClientV3Token } from "@apps/rewards/tokens";
import { JsonMimeType } from "@lib/ApiClient";
import type ApiClientInterface from "@lib/ApiClient/ApiClientInterface";
import type { Observable } from "rxjs";
import { map } from "rxjs";
import { create } from "superstruct";
import { inject, singleton } from "tsyringe";

import type {
    SpendingRulesRedeemEntity,
    SpendingRulesRefundResponse,
    SpendingRulesResponse,
    SpendingRulesStoreCreditResponse,
} from "../entities/RewardsSpendingRulesEntities";
import { SpendingRulesRedeemSchema } from "../entities/RewardsSpendingRulesRedeemSchema";
import {
    SpendingRulesRefundResponseSchema,
    SpendingRulesResponseSchema,
    SpendingRulesStoreCreditResponseSchema,
} from "../entities/RewardsSpendingRulesSchema";
import type {
    SpendingRulesQueryParams,
    SpendingRulesRedeemBody,
    SpendingRulesRefundBody,
} from "../types";

@singleton()
class RewardsSpendingRulesApiService {
    constructor(
        @inject(rewardsAuthApiClientV3Token)
        private readonly apiClientV3: ApiClientInterface
    ) {}

    getSpendingRules(
        queryParams: SpendingRulesQueryParams
    ): Observable<SpendingRulesResponse> {
        return this.apiClientV3
            .get({
                url: "/spends/getSpendingRules",
                responseType: "json",
                queryParams,
            })
            .pipe(
                map((response) =>
                    create(response.body, SpendingRulesResponseSchema)
                )
            );
    }

    redeem(
        body: SpendingRulesRedeemBody
    ): Observable<SpendingRulesRedeemEntity> {
        return this.apiClientV3
            .post({
                url: "/spends/redeemReward",
                body: JSON.stringify(body),
                responseType: "json",
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) =>
                    create(response.body, SpendingRulesRedeemSchema)
                )
            );
    }

    redeemStoreCredit(
        body: SpendingRulesRedeemBody
    ): Observable<SpendingRulesStoreCreditResponse> {
        return this.apiClientV3
            .post({
                url: "/spends/redeemStoreCredit",
                body: JSON.stringify(body),
                responseType: "json",
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) =>
                    create(
                        response.body,
                        SpendingRulesStoreCreditResponseSchema
                    )
                )
            );
    }

    refund(
        body: SpendingRulesRefundBody
    ): Observable<SpendingRulesRefundResponse> {
        return this.apiClientV3
            .post({
                url: "/spends/refundReward",
                body: JSON.stringify(body),
                responseType: "json",
                contentType: JsonMimeType,
            })
            .pipe(
                map((response) =>
                    create(response.body, SpendingRulesRefundResponseSchema)
                )
            );
    }
}

export default RewardsSpendingRulesApiService;
